import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  FormGroup,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { invoicingService } from '../services/invoicingService';
import { format } from 'date-fns';

const steps = ['Search Vehicle', 'Select Booking', 'Confirm & Generate'];

const GenerateInvoiceModal = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [vrn, setVrn] = useState('');
  const [searching, setSearching] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [regenerateConfirmed, setRegenerateConfirmed] = useState(false);

  const selectedBooking = bookings.find((b) => b.id === selectedBookingId);

  const handleClose = () => {
    setActiveStep(0);
    setVrn('');
    setSearching(false);
    setVehicle(null);
    setBookings([]);
    setSelectedBookingId(null);
    setGenerating(false);
    setError(null);
    setSendEmail(true);
    setRegenerateConfirmed(false);
    onClose();
  };

  const handleSearch = async () => {
    if (!vrn.trim()) {
      setError('Please enter a vehicle registration number');
      return;
    }

    setSearching(true);
    setError(null);

    try {
      const data = await invoicingService.searchBookingsByVRN(vrn.trim());
      setVehicle(data.vehicle);
      setBookings(data.bookings);
      setActiveStep(1);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to search';
      setError(errorMsg);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectBooking = () => {
    if (!selectedBookingId) {
      setError('Please select a booking');
      return;
    }
    setError(null);
    setActiveStep(2);
  };

  const handleGenerate = async () => {
    if (!selectedBookingId) return;

    // If booking has existing invoice and user hasn't confirmed regeneration
    if (selectedBooking?.has_existing_invoice && !regenerateConfirmed) {
      setError('Please confirm that you want to regenerate the invoice');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const result = await invoicingService.generateInvoiceFromBooking(selectedBookingId, {
        regenerate: selectedBooking?.has_existing_invoice || false,
        sendEmail,
      });

      onSuccess(result);
      handleClose();
    } catch (err) {
      if (err.response?.status === 409) {
        // Invoice exists - shouldn't happen if we handle it properly
        setError('An invoice already exists. Please confirm regeneration.');
      } else {
        const errorMsg = err.response?.data?.error || err.message || 'Failed to generate invoice';
        setError(errorMsg);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleBack = () => {
    setError(null);
    if (activeStep === 1) {
      setSelectedBookingId(null);
    }
    if (activeStep === 2) {
      setRegenerateConfirmed(false);
    }
    setActiveStep((prev) => prev - 1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && activeStep === 0) {
      handleSearch();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      confirmed: { color: 'info', label: 'Confirmed' },
      assigned: { color: 'primary', label: 'Assigned' },
      in_progress: { color: 'warning', label: 'In Progress' },
      waiting_parts: { color: 'warning', label: 'Waiting Parts' },
      pending_admin_review: { color: 'info', label: 'Pending Review' },
      awaiting_payment: { color: 'warning', label: 'Awaiting Payment' },
      completed: { color: 'success', label: 'Completed' },
      cancelled: { color: 'error', label: 'Cancelled' },
    };

    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter the vehicle registration number to search for bookings.
            </Typography>
            <Box display="flex" gap={2} alignItems="flex-start">
              <TextField
                fullWidth
                label="Vehicle Registration"
                placeholder="e.g., AB12 CDE"
                value={vrn}
                onChange={(e) => setVrn(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                disabled={searching}
                autoFocus
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={searching || !vrn.trim()}
                startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ minWidth: 120, height: 56 }}
              >
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            {vehicle && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Vehicle Found
                </Typography>
                <Typography variant="h6">
                  {vehicle.registration} - {vehicle.make} {vehicle.model}{' '}
                  {vehicle.year && `(${vehicle.year})`}
                </Typography>
              </Paper>
            )}

            <Typography variant="body2" color="text.secondary" mb={2}>
              Select a booking to generate an invoice for:
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Invoice</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <RadioGroup
                    value={selectedBookingId || ''}
                    onChange={(e) => setSelectedBookingId(parseInt(e.target.value))}
                  >
                    {bookings.map((booking) => (
                      <TableRow
                        key={booking.id}
                        hover
                        onClick={() => setSelectedBookingId(booking.id)}
                        sx={{ cursor: 'pointer' }}
                        selected={selectedBookingId === booking.id}
                      >
                        <TableCell padding="checkbox">
                          <Radio value={booking.id} />
                        </TableCell>
                        <TableCell>{formatDate(booking.booking_date)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{booking.customer_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.customer_email}
                          </Typography>
                        </TableCell>
                        <TableCell>{getStatusChip(booking.status)}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(booking.total_cost)}
                        </TableCell>
                        <TableCell>
                          {booking.has_existing_invoice ? (
                            <Chip
                              icon={<WarningIcon />}
                              label={booking.existing_invoice_number}
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Chip label="No Invoice" size="small" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </RadioGroup>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirm Invoice Generation
            </Typography>

            {selectedBooking && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Vehicle
                    </Typography>
                    <Typography variant="body1">
                      {vehicle?.registration} - {vehicle?.make} {vehicle?.model}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Booking Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedBooking.booking_date)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Customer
                    </Typography>
                    <Typography variant="body1">{selectedBooking.customer_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedBooking.customer_email}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box>{getStatusChip(selectedBooking.status)}</Box>
                  </Box>
                </Box>
              </Paper>
            )}

            {selectedBooking?.has_existing_invoice && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Existing Invoice: {selectedBooking.existing_invoice_number}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  This booking already has an invoice. Generating a new invoice will{' '}
                  <strong>delete the existing invoice</strong> and create a new one with a new
                  invoice number.
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={regenerateConfirmed}
                        onChange={(e) => setRegenerateConfirmed(e.target.checked)}
                      />
                    }
                    label="I understand and want to regenerate the invoice"
                  />
                </FormGroup>
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                }
                label="Send invoice email to customer"
              />
            </FormGroup>
          </Box>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return !!vrn.trim();
      case 1:
        return !!selectedBookingId;
      case 2:
        if (selectedBooking?.has_existing_invoice) {
          return regenerateConfirmed;
        }
        return true;
      default:
        return false;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptIcon />
          <span>Generate Invoice</span>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={generating}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={generating}>
            Back
          </Button>
        )}
        {activeStep < 2 ? (
          <Button
            variant="contained"
            onClick={activeStep === 0 ? handleSearch : handleSelectBooking}
            disabled={!canProceed() || searching}
          >
            {activeStep === 0 ? 'Search' : 'Next'}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerate}
            disabled={!canProceed() || generating}
            startIcon={generating ? <CircularProgress size={20} /> : <ReceiptIcon />}
          >
            {generating ? 'Generating...' : 'Generate Invoice'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateInvoiceModal;
